import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import AddEngineerModal from '../../components/features/engineers/AddEngineerModal';
import EditEngineerModal from '../../components/features/engineers/EditEngineerModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../constants/roles';

const EngineersView = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [engineers, setEngineers] = useState([]);
  const [filteredEngineers, setFilteredEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'ALL');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);

  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadEngineers();
  }, [user?.id]);

  useEffect(() => {
    filterEngineers();
  }, [searchTerm, statusFilter, engineers]);

  const loadEngineers = async () => {
    setLoading(true);
    const teamLeadId = user?.role === ROLES.TL ? user.id : null;
    const result = await engineerService.getAllEngineers(teamLeadId);
    if (result.success) {
      setEngineers(result.data);
      setFilteredEngineers(result.data);
    }
    setLoading(false);
  };

  const filterEngineers = () => {
    let filtered = [...engineers];

    if (searchTerm) {
      filtered = filtered.filter(engineer =>
        engineer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        engineer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        engineer.team_lead?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter === 'WORKING') {
      filtered = filtered.filter(engineer => engineer.status !== 'On Leave');
    } else if (statusFilter !== 'ALL') {
      filtered = filtered.filter(engineer => engineer.status === statusFilter);
    }

    setFilteredEngineers(filtered);
  };

  const handleAddEngineer = async (engineerData) => {
  const result = await engineerService.createEngineer(engineerData);
  if (result.success) {
    success('Engineer added successfully!');
    await loadEngineers();
  } else {
    error(result.error || 'Failed to add engineer');
  }
};

const handleEditEngineer = (engineer) => {
  setSelectedEngineer(engineer);
  setShowEditModal(true);
};

const handleUpdateEngineer = async (engineerId, engineerData) => {
  const result = await engineerService.updateEngineer(engineerId, engineerData);
  if (!result.success) {
    error(result.error || 'Failed to update engineer');
  }
  return result;
};

const handleDeleteEngineer = (engineer) => {
  setSelectedEngineer(engineer);
  setShowDeleteDialog(true);
};

  const handleDeleteConfirm = async () => {
  const result = await engineerService.deleteEngineer(selectedEngineer.id);
  if (result.success) {
    success('Engineer deleted successfully!');
    await loadEngineers();
  } else {
    error(result.error || 'Failed to delete engineer');
  }
  setShowDeleteDialog(false);
  setSelectedEngineer(null);
};

const handleViewPerformance = async (engineer) => {
  const result = await engineerService.getEngineerPerformance(engineer.id);
  if (result.success) {
    // You can show a performance modal here
    console.log('Performance data:', result.data);
    success('Performance data loaded!');
  } else {
    error(result.error || 'Failed to load performance data');
  }
};

  const getEngineerStats = () => {
    const total = engineers.length;
    const active = engineers.filter(e => e.is_on_leave_now === false).length;
    const available = engineers.filter(e => e.status === 'Available').length;
    const avgPerformance = engineers.filter(e => e.is_active === true).reduce((sum, e) => sum + (e.performance_score || 0), 0) / total || 0;
    
    const engineersWithTime = engineers.filter((e) => (e.avg_response_time || 0) > 0);
    const avgResponseTime = engineersWithTime.reduce((sum, e) => sum + (e.avg_response_time || 0), 0) / engineersWithTime.length || 0;

    return { 
      total, 
      active, 
      available, 
      avgPerformance: avgPerformance.toFixed(1),
      avgResponseTime: avgResponseTime.toFixed(1)
    };
  };

  const stats = getEngineerStats();

  const handleEngineerCardClick = (filter) => {
    setSearchTerm('');
    setStatusFilter(filter);

    if (filter === 'ALL') {
      setSearchParams({});
    } else {
      setSearchParams({ status: filter });
    }
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  const selectStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#10b981';
      case 'Available': return '#3b82f6';
      case 'Busy': return '#f59e0b';
      case 'On Leave': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Engineer',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px'
          }}>
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {row.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${getStatusColor(value)}20`,
          color: getStatusColor(value)
        }}>
          {value}
        </span>
      )
    },
    // {
    //   key: 'location',
    //   label: 'Location',
    //   render: (value) => (
    //     <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    //        {value}
    //     </span>
    //   )
    // },
    {
    key: 'team_lead',
    label: 'Team Lead/Manager',
    render: (value) => {
      if (!value) {
        return (
          <span style={{ 
            color: 'var(--text-secondary)', 
            fontStyle: 'italic',
            fontSize: '13px'
          }}>
            Not Assigned
          </span>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>{value.name}</span>
        </div>
      );
    }
  },
    {
      key: 'active_tickets',
      label: 'Active Tickets',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          background: 'var(--surface-light)',
          color: 'var(--text-primary)'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'completed_this_month',
      label: 'Completed',
      render: (value) => `${value} tickets`
    },
    {
      key: 'performance_score',
      label: 'Performance',
      render: (value) => {
        const color = value >= 90 ? '#10b981' : value >= 75 ? '#f59e0b' : '#ef4444';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              flex: 1,
              height: '8px',
              background: 'var(--surface-light)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${value}%`,
                height: '100%',
                background: color,
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '700', color, minWidth: '35px' }}>
              {value}%
            </span>
          </div>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading engineers...
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={gridStyle}>
        <StatCard
          title="Total Engineers"
          value={stats.total}
          icon=""
          color="#3b82f6"
          onClick={() => handleEngineerCardClick('ALL')}
        />
        <StatCard
          title="Active Today"
          value={stats.active}
          icon=""
          subtitle="Currently working"
          color="#10b981"
          onClick={() => handleEngineerCardClick('WORKING')}
        />
        {/* <StatCard
          title="Available"
          value={stats.available}
          icon=""
          subtitle="Ready for assignment"
          color="#06b6d4"
        /> */}
        <StatCard
          title="Avg Performance"
          value={`${stats.avgPerformance}%`}
          icon=""
          color="#f59e0b"
        />
        <StatCard
          title="Avg Response Time"
          value={`${stats.avgResponseTime} hrs`}
          icon=""
          color="#06b6d4"
        />
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search engineers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        <div style={filterGroupStyle}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
            Filter by Status
          </label>
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            {/* <option value="Available">Available</option> */}
            <option value="Busy">Busy</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <Button variant="secondary" onClick={loadEngineers}>
             Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
             Add Engineer
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredEngineers}
        actions={[
          // { label: ' Performance', onClick: handleViewPerformance },
          { label: ' Edit', onClick: handleEditEngineer },
          { label: ' Delete', onClick: handleDeleteEngineer }
        ]}
        pageSize={15}
      />

      {/* Modals */}
      <AddEngineerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddEngineer}
      />

      <EditEngineerModal
        isOpen={showEditModal}
        onClose={(shouldRefresh) => {
          setShowEditModal(false);
          setSelectedEngineer(null);
          if (shouldRefresh === true) {
            loadEngineers();
          }
        }}
        onSubmit={handleUpdateEngineer}
        engineer={selectedEngineer}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedEngineer(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Engineer"
        message={
          selectedEngineer?.active_tickets > 0
            ? `Warning: Engineer ${selectedEngineer?.name} has ${selectedEngineer.active_tickets} active ticket(s). Deactivating them will prevent further assignments but they will remain in history. Are you sure?`
            : `Are you sure you want to deactivate engineer ${selectedEngineer?.name}? They will no longer be able to log in or receive new tickets.`
        }
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default EngineersView;
